/**
 * Rollback service — wraps the five rollback API endpoints.
 *
 * USE_ROLLBACK_MOCK_DATA controls whether real HTTP calls are made or
 * whether the functions resolve mock data immediately (with a small
 * artificial delay to simulate network latency).
 *
 * Set USE_ROLLBACK_MOCK_DATA = false once the backend endpoints are built.
 */

import { authenticatedFetch } from '../../utils/authenticatedFetch';
import { getV2AuthHeaders } from '../../utils/authHeaders';
import { getEnv } from '../../config/env';
import type { Migration } from '../../types/migration';
import type { RollbackPlanResponse, RollbackQuickPreview } from '../../types/rollback';
import {
  MOCK_ROLLBACK_MIGRATIONS,
  MOCK_ROLLBACK_PLAN,
  MOCK_ROLLBACK_QUICK_PREVIEW
} from '../../utils/mockRollbackData';

// ---------------------------------------------------------------------------
// Feature flag — flip to false when the real backend endpoints are ready
// ---------------------------------------------------------------------------

const USE_ROLLBACK_MOCK_DATA = false;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Simulates API latency when returning mock data. */
const mockDelay = (ms = 400): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

// ---------------------------------------------------------------------------
// getRollbackQuickPreview
// ---------------------------------------------------------------------------

/**
 * Fetches a lightweight preview of what a rollback would affect.
 * GET /api/migrations/{id}/rollback-preview
 */
export async function getRollbackQuickPreview(
  token: string,
  migrationId: string
): Promise<RollbackQuickPreview> {
  if (USE_ROLLBACK_MOCK_DATA) {
    await mockDelay();
    return MOCK_ROLLBACK_QUICK_PREVIEW(migrationId);
  }

  try {
    const baseUrl = getEnv().VITE_EDCI_API_URL;
    const url = `${baseUrl}/api/migrations/${encodeURIComponent(migrationId)}/rollback-preview`;

    const response = await authenticatedFetch(url, {
      method: 'GET',
      headers: getV2AuthHeaders(token)
    });

    if (!response.ok) {
      throw new Error(
        `Error fetching rollback preview: ${response.status} ${response.statusText}`
      );
    }

    const data: RollbackQuickPreview = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching rollback preview:', error);
    throw error;
  }
}

// ---------------------------------------------------------------------------
// startRollback
// ---------------------------------------------------------------------------

/**
 * Triggers the rollback workflow for a migration (queues job4RollbackSetup).
 * POST /api/migrations/{id}/rollback
 * Returns the updated Migration record.
 */
export async function startRollback(token: string, migrationId: string): Promise<Migration> {
  if (USE_ROLLBACK_MOCK_DATA) {
    await mockDelay();
    const mockEntry = MOCK_ROLLBACK_MIGRATIONS.find((m) => m.id === migrationId);
    if (!mockEntry) {
      throw new Error(`Mock migration not found: ${migrationId}`);
    }
    return { ...mockEntry, status: 'RollbackQueued' };
  }

  try {
    const baseUrl = getEnv().VITE_EDCI_API_URL;
    const url = `${baseUrl}/api/migrations/${encodeURIComponent(migrationId)}/rollback`;

    const response = await authenticatedFetch(url, {
      method: 'POST',
      headers: {
        ...getV2AuthHeaders(token),
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Error starting rollback: ${response.status} ${response.statusText}`);
    }

    const data: Migration = await response.json();
    return data;
  } catch (error) {
    console.error('Error starting rollback:', error);
    throw error;
  }
}

// ---------------------------------------------------------------------------
// getRollbackStatus
// ---------------------------------------------------------------------------

/**
 * Polls the current rollback status for a migration.
 * GET /api/migrations/{id}/rollback-status
 * Returns the updated Migration record (caller reads `.status` from it).
 */
export async function getRollbackStatus(token: string, migrationId: string): Promise<Migration> {
  if (USE_ROLLBACK_MOCK_DATA) {
    await mockDelay(200);
    const mockEntry = MOCK_ROLLBACK_MIGRATIONS.find((m) => m.id === migrationId);
    if (!mockEntry) {
      throw new Error(`Mock migration not found: ${migrationId}`);
    }
    // Simulate Job 4 completing — return RollbackPlanAwaitingApproval so the
    // preview page polling terminates and fetches the plan.
    return { ...mockEntry, status: 'RollbackPlanAwaitingApproval' };
  }

  try {
    const baseUrl = getEnv().VITE_EDCI_API_URL;
    const url = `${baseUrl}/api/migrations/${encodeURIComponent(migrationId)}/rollback-status`;

    const response = await authenticatedFetch(url, {
      method: 'GET',
      headers: getV2AuthHeaders(token)
    });

    if (!response.ok) {
      throw new Error(
        `Error fetching rollback status: ${response.status} ${response.statusText}`
      );
    }

    const data: Migration = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching rollback status:', error);
    throw error;
  }
}

// ---------------------------------------------------------------------------
// getRollbackPlan
// ---------------------------------------------------------------------------

/**
 * Retrieves the full rollback plan (all resources and their actions).
 * GET /api/migrations/{id}/rollback-plan
 */
export async function getRollbackPlan(
  token: string,
  migrationId: string
): Promise<RollbackPlanResponse> {
  if (USE_ROLLBACK_MOCK_DATA) {
    await mockDelay();
    return MOCK_ROLLBACK_PLAN(migrationId);
  }

  try {
    const baseUrl = getEnv().VITE_EDCI_API_URL;
    const url = `${baseUrl}/api/migrations/${encodeURIComponent(migrationId)}/rollback-plan`;

    const response = await authenticatedFetch(url, {
      method: 'GET',
      headers: getV2AuthHeaders(token)
    });

    if (!response.ok) {
      throw new Error(`Error fetching rollback plan: ${response.status} ${response.statusText}`);
    }

    const data: RollbackPlanResponse = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching rollback plan:', error);
    throw error;
  }
}

// ---------------------------------------------------------------------------
// approveRollback
// ---------------------------------------------------------------------------

/**
 * Approves the rollback plan, triggering job5RollbackApply.
 * POST /api/migrations/{id}/rollback-approve
 * Returns the updated Migration record.
 */
export async function approveRollback(token: string, migrationId: string): Promise<Migration> {
  if (USE_ROLLBACK_MOCK_DATA) {
    await mockDelay();
    const mockEntry = MOCK_ROLLBACK_MIGRATIONS.find((m) => m.id === migrationId);
    if (!mockEntry) {
      throw new Error(`Mock migration not found: ${migrationId}`);
    }
    return { ...mockEntry, status: 'RollbackInProgress' };
  }

  try {
    const baseUrl = getEnv().VITE_EDCI_API_URL;
    const url = `${baseUrl}/api/migrations/${encodeURIComponent(migrationId)}/rollback-approve`;

    const response = await authenticatedFetch(url, {
      method: 'POST',
      headers: {
        ...getV2AuthHeaders(token),
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Error approving rollback: ${response.status} ${response.statusText}`);
    }

    const data: Migration = await response.json();
    return data;
  } catch (error) {
    console.error('Error approving rollback:', error);
    throw error;
  }
}
